import { Inject, Injectable } from '@angular/core';
import { Route, Routes } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import Debug from 'debug';
import { of } from 'rxjs';
import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { APP_ROUTES, AumicoUser } from '../../interfaces/aumico.routing';
import { AumicoLayoutConfig } from '../interfaces/layout.config';
import * as fromActions from './aumico.layout.action';
import {
    INavigationEvent,
    initRouterConfig,
    IUpdateNavigationEvent,
    IUpdateUserEvent,
    updateSubNavigation,
} from './aumico.layout.action';
import * as fromReducer from './aumico.layout.reducer';
import { ModuleState } from './aumico.layout.reducer';

const debug = Debug('aumico:aumico-core:RoutingEffects');

@Injectable()
export class RoutingEffects {
    private currentLanguage: string;
    constructor(
        private actions$: Actions,
        private store$: Store<ModuleState>,
        @Inject(APP_ROUTES) private config: Routes,
    ) {
        this.currentLanguage = localStorage.getItem('currentLanguage');
        // TODO: the navigation service is only injected, that it will be initialized.
        debug('Effect initialized');
        this.store$.dispatch(initRouterConfig({ payload: this.config }));
    }

    updatePrimaryNavigationAfterRouterConfigInitialization$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.initRouterConfig.type),
            withLatestFrom(this.store$.select(fromReducer.selectUser)),
            mergeMap(([action, user]: [fromActions.ActionWithPayload<Routes>, AumicoUser]) => {
                debug('updatePrimaryNavigationAfterRouterConfigInitialization$', action, user);
                debug(user);
                const config = action.payload;
                let roles: string[] = [];
                if (user != null && user.roles != null) {
                    roles = user.roles;
                }
                const primary = config.filter((route: Route) => (route.data as AumicoLayoutConfig)?.type === 'primary');
                const navigation = constructNavigationItemsByRoles(primary, roles, user?.language);
                return of(fromActions.updatePrimaryNavigation({ payload: navigation }));
            }),
        ),
    );

    updateSecondaryNavigationAfterRouterConfigInitialization$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.initRouterConfig.type),
            withLatestFrom(this.store$.select(fromReducer.selectUser)),
            mergeMap(([action, user]: [fromActions.ActionWithPayload<Routes>, AumicoUser]) => {
                debug('updateSecondaryNavigationAfterRouterConfigInitialization$', action, user);
                const config = action.payload;
                let roles: string[] = [];
                if (user != null && user.roles != null) {
                    roles = user.roles;
                }
                debug(user);
                const secondary = config.filter(
                    (route: Route) => (route.data as AumicoLayoutConfig)?.type === 'secondary',
                );
                const navigation = constructNavigationItemsByRoles(secondary, roles, user?.language);
                return of(fromActions.updateSecondaryNavigation({ payload: navigation }));
            }),
        ),
    );
    updateUserNavigationAfterRouterConfigInitialization$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.initRouterConfig.type),
            withLatestFrom(this.store$.select(fromReducer.selectUser)),
            mergeMap(([action, user]: [fromActions.ActionWithPayload<Routes>, AumicoUser]) => {
                debug('updateUserNavigationAfterRouterConfigInitialization$ 1', action, user);
                const config = action.payload;
                let roles: string[] = [];
                if (user != null && user.roles != null) {
                    roles = user.roles;
                }
                const userRoute = config.filter((route: Route) => (route.data as AumicoLayoutConfig)?.type === 'user');
                const navigation = constructNavigationItemsByRoles(userRoute, roles, user?.language);
                if (navigation.length > 0) {
                    return of(fromActions.updateUserNavigation({ payload: navigation[0] }));
                } else {
                    return of(fromActions.updateUserNavigation({ payload: null }));
                }
            }),
        ),
    );
    updatePrimaryNavigationOnRoleChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateUser.type),
            withLatestFrom(this.store$.select(fromReducer.selectRouterConfig)),
            mergeMap(([action, routerConfig]: [fromActions.ActionWithPayload<IUpdateUserEvent>, Routes]) => {
                let userData: AumicoUser | undefined;
                let roles: string[] = [];
                if (action.payload != null && action.payload.user != null) {
                    userData = action.payload.user;
                    roles = userData.roles;
                }
                const config = routerConfig;
                debug('updatePrimaryNavigationOnRoleChange$ Role', roles, routerConfig);
                const primaryRoutes = config.filter((route: Route) => {
                    return (route.data as AumicoLayoutConfig)?.type === 'primary';
                });
                const navigation = constructNavigationItemsByRoles(
                    primaryRoutes,
                    roles,
                    userData != null ? userData.language : this.currentLanguage,
                );
                return of(fromActions.updatePrimaryNavigation({ payload: navigation }));
            }),
        ),
    );

    updateSecondaryNavigationOnRoleChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateUser.type),
            withLatestFrom(this.store$.select(fromReducer.selectRouterConfig)),
            mergeMap(([action, routerConfig]: [fromActions.ActionWithPayload<IUpdateUserEvent>, Routes]) => {
                let userData: AumicoUser | undefined;
                let roles: string[] = [];
                if (action.payload != null && action.payload.user != null) {
                    userData = action.payload.user;
                    roles = userData.roles;
                }
                const config = routerConfig;
                debug('updateSecondaryNavigationOnRoleChange$ Role', roles, routerConfig);
                const secondaryRoutes = config.filter(
                    (route: Route) => (route.data as AumicoLayoutConfig)?.type === 'secondary',
                );
                const navigation = constructNavigationItemsByRoles(
                    secondaryRoutes,
                    roles,
                    userData != null ? userData.language : this.currentLanguage,
                );
                return of(fromActions.updateSecondaryNavigation({ payload: navigation }));
            }),
        ),
    );

    updateUserNavigationOnRoleChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateUser.type),
            withLatestFrom(this.store$.select(fromReducer.selectRouterConfig)),
            mergeMap(([action, routerConfig]: [fromActions.ActionWithPayload<IUpdateUserEvent>, Routes]) => {
                let userData: AumicoUser | undefined;
                let roles: string[] = [];
                if (action.payload != null && action.payload.user != null) {
                    userData = action.payload.user;
                    roles = userData.roles;
                }
                const config = routerConfig;
                debug('updateUserNavigationOnRoleChange$ Role 1', roles, routerConfig);
                const userRoute = config.filter((route: Route) => (route.data as AumicoLayoutConfig)?.type === 'user');
                const navigation = constructNavigationItemsByRoles(
                    userRoute,
                    roles,
                    userData != null ? userData.language : this.currentLanguage,
                );

                if (navigation.length > 0) {
                    return of(fromActions.updateUserNavigation({ payload: navigation[0] }));
                } else {
                    return of(fromActions.updateUserNavigation({ payload: null }));
                }
            }),
        ),
    );

    updateLayoutOnUserChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.updateUser.type),
            mergeMap((action: { payload: IUpdateUserEvent }) => {
                // Actions format: { payload: AumicoUser }

                debug('updateLayoutOnUserChange$ User', action);
                let layoutIsHidden = false;
                if (action.payload.user === undefined) {
                    layoutIsHidden = true;
                }
                return of(fromActions.updateLayoutIsHidden({ payload: layoutIsHidden }));
            }),
        ),
    );

    //updateSubNavigation when main navigation changes
    updateLayoutOnMainNavigationUpdate = createEffect(() =>
        this.actions$.pipe(
            ofType(fromActions.onPageNavigation.type),
            mergeMap((action: { payload: INavigationEvent }) => {
                debug('updateLayoutOnMainNavigationUpdate page', action);
                const primaryNavigationElement = action.payload.route;

                if (primaryNavigationElement.children) {
                    const sub: Routes = primaryNavigationElement.children.filter(
                        (route: Route) => (route.data as AumicoLayoutConfig)?.type === 'sub',
                    );

                    debug('Nr Of Subnav:', sub.length, sub);
                    const updateNavigationEvent: IUpdateNavigationEvent = {
                        items: sub,
                    };
                    return of(updateSubNavigation({ payload: updateNavigationEvent }));
                } else {
                    const updateNavigationEvent: IUpdateNavigationEvent = {
                        items: [],
                    };
                    return of(updateSubNavigation({ payload: updateNavigationEvent }));
                }
            }),
        ),
    );
}

function filterByRole(route: Route, role: string): boolean {
    // Guard
    if ((route.data as AumicoLayoutConfig)?.canSeeNavigationLinkRoles === undefined) {
        return true;
    }
    // Guard
    if ((route.data as AumicoLayoutConfig)?.canSeeNavigationLinkRoles.length < 1) {
        return true;
    }
    // actual test
    for (const routeElement of (route.data as AumicoLayoutConfig)?.canSeeNavigationLinkRoles) {
        if (routeElement === role) {
            return true;
        }
    }
    return false;
}

function replaceLanguage(dynamicUrl: string, language: string): string {
    return dynamicUrl.split(':language').join(language);
}

function constructNavigationItemsByRoles(routes: Route[], roles: string[], currentLanguage: string): Routes {
    const routesSet = new Set<Route>();
    if (roles.length > 0) {
        roles.forEach((role) => {
            const filteredRoutesByRole = routes.filter((route: Route) => filterByRole(route, role));
            filteredRoutesByRole.forEach((filteredRoute) => routesSet.add(filteredRoute));
        });
    }
    const filteredRoutes = routesSet.values();

    const navigation: Routes = [];
    Array.from(filteredRoutes).forEach((route: Route) => {
        debug('filteredroute', route);
        const link = replaceLanguage(route.path, currentLanguage);
        const item: Route = {
            path: link,
            data: {
                label: (route.data as AumicoLayoutConfig)?.label,
                icon: (route.data as AumicoLayoutConfig)?.icon,
            },
        };
        navigation.push(item);
    });
    return navigation;
}
